import { ITasksTableRow } from "@/src/types/tasksTable";

/** UI column key → field on table row (same pattern as Applications column id → IApplyInfoData). */
export const TASKS_TABLE_COLUMN_DATA_KEY: Record<string, keyof ITasksTableRow> = {
  date: "created_date",
  taskId: "task_id",
  studentId: "student_id",
  fullName: "full_name",
  dateOfBirth: "date_of_birth",
  studentStatus: "student_status",
  assigneeId: "assignee_id",
  assignee: "assignee_email",
  executorId: "executor_id",
  executorEmail: "executor_email",
  type: "type",
  intake: "intake",
  admissionSemester: "admission_semester",
  formOfEducation: "form_of_education",
  levelEducation: "level_education",
  course: "course",
  country: "category",
  mediumOfInstruction: "medium_of_instruction",
  duration: "duration",
  transferFee: "transfer_fee",
  certificationEqualization: "certification_equalization",
  preparatoryDepartment: "preparatory_department",
  updated: "priority",
  note: "note",
  deadlineDate: "deadline_date",
  status: "status",
  office: "form_office",
  formOffice: "form_office",
  fullNameNat: "fullname_nat",
  nationality: "nationality",
  selectedCourse: "selected_course",
  tuitionFee: "tuition_fee",
  medicalInsurance: "medical_insurance",
  residencePermit: "residence_permit",
  otherExpenses: "other_expenses",
  acommodation: "acommodation",
  admissionFee: "admission_fee",
  completeOneYear: "complete_one_year",
};

export type TasksColumnConfig = {
  title: string;
  id: string;
  width?: number;
};

const defaultWidth = 5;

const col = (id: string, title: string, width = defaultWidth): TasksColumnConfig => ({
  id,
  title,
  width,
});

/** All columns available in Tasks table settings (like onlineApplicationAllFieldsList). */
export const tasksAllFieldsList: TasksColumnConfig[] = [
  col("date", "Created Date"),
  col("taskId", "Task Id"),
  col("studentId", "Student Id"),
  col("fullName", "Full Name"),
  col("fullNameNat", "Fullname Nat"),
  col("dateOfBirth", "Date of Birth"),
  col("studentStatus", "Student Status"),
  col("assigneeId", "Assignee Id"),
  col("assignee", "Assignee"),
  col("executorId", "Executor Id"),
  col("executorEmail", "Executor Email"),
  col("type", "Task Type"),
  col("intake", "Intake"),
  col("admissionSemester", "Admission Semester"),
  col("formOfEducation", "Form of Education"),
  col("levelEducation", "Degree"),
  col("course", "Course"),
  col("country", "Category"),
  col("mediumOfInstruction", "Medium of Instruction"),
  col("duration", "Duration"),
  col("transferFee", "Transfer Fee"),
  col("certificationEqualization", "Certification & Equalization"),
  col("preparatoryDepartment", "Preparatory Department"),
  col("updated", "Priority"),
  col("note", "Note"),
  col("deadlineDate", "Deadline"),
  col("status", "Status"),
  col("office", "Office"),
  col("formOffice", "Form Office"),
  col("nationality", "Nationality"),
  col("selectedCourse", "Selected Course"),
  col("tuitionFee", "Tuition Fee"),
  col("medicalInsurance", "Medical Insurance"),
  col("residencePermit", "Residence Permit"),
  col("otherExpenses", "Other Expenses"),
  col("acommodation", "Acommodation"),
  col("admissionFee", "Admission Fee"),
  col("completeOneYear", "Complete One Year"),
];

/** Default visible columns when user has no saved TOPConfig (like onlineApplicationsDefaultConfiguration). */
export const tasksDefaultConfiguration: TasksColumnConfig[] = [
  { id: "date", title: "Created Date", width: 4.81 },
  { id: "taskId", title: "Task Id", width: 4.5 },
  { id: "studentId", title: "Student Id", width: 4.87 },
  { id: "fullName", title: "Full Name", width: 11.5 },
  { id: "fullNameNat", title: "Fullname Nat", width: 11.5 },
  { id: "dateOfBirth", title: "Date of Birth", width: 7.5 },
  { id: "studentStatus", title: "Student Status", width: 6.25 },
  { id: "status", title: "Status", width: 6.25 },
  { id: "type", title: "Task Type", width: 6.25 },
  { id: "levelEducation", title: "Degree", width: 7.5 },
  { id: "course", title: "Course", width: 9.69 },
  { id: "country", title: "Category", width: 7.44 },
  { id: "updated", title: "Priority", width: 3.87 },
  { id: "deadlineDate", title: "Deadline", width: 8.75 },
  { id: "assignee", title: "Assignee", width: 6 },
];

const tasksAllFieldsById = Object.fromEntries(tasksAllFieldsList.map((c) => [c.id, c])) as Record<string, TasksColumnConfig>;

/** Saved/API/legacy column id → UI column id. */
export const tasksLegacyColumnIdMap: Record<string, string> = {
  created_date: "date",
  task_id: "taskId",
  student_id: "studentId",
  full_name: "fullName",
  date_of_birth: "dateOfBirth",
  student_status: "studentStatus",
  assignee_id: "assigneeId",
  assignee_email: "assignee",
  executor_id: "executorId",
  executor_email: "executorEmail",
  admission_semester: "admissionSemester",
  form_of_education: "formOfEducation",
  level_education: "levelEducation",
  medium_of_instruction: "mediumOfInstruction",
  transfer_fee: "transferFee",
  certification_equalization: "certificationEqualization",
  preparatory_department: "preparatoryDepartment",
  deadline_date: "deadlineDate",
  form_office: "formOffice",
  fullname_nat: "fullNameNat",
  selected_course: "selectedCourse",
  tuition_fee: "tuitionFee",
  medical_insurance: "medicalInsurance",
  residence_permit: "residencePermit",
  other_expenses: "otherExpenses",
  admission_fee: "admissionFee",
  complete_one_year: "completeOneYear",
  category: "country",
  priority: "updated",
  fullName: "fullName",
  dateOfBirth: "dateOfBirth",
  email: "deadlineDate",
};

export const getTasksColumnCellValue = (row: ITasksTableRow, columnId: string): string => {
  const dataKey = TASKS_TABLE_COLUMN_DATA_KEY[columnId];
  if (!dataKey) return "";
  const value = row[dataKey];
  if (value == null) return "";
  return typeof value === "number" ? String(value) : String(value);
};

export const resolveTasksColumnConfig = (column: TasksColumnConfig): TasksColumnConfig => {
  const uiId = tasksLegacyColumnIdMap[column.id] ?? column.id;
  const canonical = tasksAllFieldsById[uiId];
  if (!canonical) return column;
  return {
    ...column,
    id: canonical.id,
    title: canonical.title,
    width: column.width ?? canonical.width,
  };
};

const insertColumnAfter = (columns: TasksColumnConfig[], column: TasksColumnConfig, afterId: string): TasksColumnConfig[] => {
  const anchorIdx = columns.findIndex((c) => c.id === afterId);
  const insertAt = anchorIdx === -1 ? columns.length : anchorIdx + 1;
  return [...columns.slice(0, insertAt), column, ...columns.slice(insertAt)];
};

const insertColumnBefore = (columns: TasksColumnConfig[], column: TasksColumnConfig, beforeId: string): TasksColumnConfig[] => {
  const beforeIdx = columns.findIndex((c) => c.id === beforeId);
  if (beforeIdx === -1) return [...columns, column];
  const next = [...columns];
  next.splice(beforeIdx, 0, column);
  return next;
};

const findTasksColumn = (id: string): TasksColumnConfig | undefined =>
  tasksAllFieldsById[id] ?? tasksDefaultConfiguration.find((col) => col.id === id);

const hasLegacyColumnIds = (columns: TasksColumnConfig[]): boolean =>
  columns.some((col) => {
    const mapped = tasksLegacyColumnIdMap[col.id];
    return mapped != null && mapped !== col.id;
  });

/** Migrates saved TOPTable: legacy ids → UI keys and canonical titles. */
export function normalizeVisibleTOPColumns(columns: TasksColumnConfig[]): TasksColumnConfig[] {
  const knownIds = new Set(tasksAllFieldsList.map((col) => col.id));
  const legacyMigration = hasLegacyColumnIds(columns);

  let next = columns
    .map((col) => resolveTasksColumnConfig(col))
    .filter((col) => knownIds.has(col.id));

  // Forward migration: ensure the newly added Fullname Nat column is visible by default
  // even for users with previously saved TOP config.
  if (!next.some((col) => col.id === "fullNameNat")) {
    const fullNameNatCol = tasksAllFieldsList.find((c) => c.id === "fullNameNat");
    if (fullNameNatCol) next = insertColumnAfter(next, { ...fullNameNatCol }, "fullName");
  }

  // Forward migration: Student Status after Date of Birth.
  if (next.some((col) => col.id === "dateOfBirth") && !next.some((col) => col.id === "studentStatus")) {
    const studentStatusCol = tasksAllFieldsList.find((c) => c.id === "studentStatus");
    if (studentStatusCol) next = insertColumnAfter(next, { ...studentStatusCol }, "dateOfBirth");
  }

  const courseIndex = next.findIndex((col) => col.id === "course");
  let degreeIndex = next.findIndex((col) => col.id === "levelEducation");
  if (courseIndex !== -1 && degreeIndex === -1) {
    const degreeColumn = findTasksColumn("levelEducation");
    if (degreeColumn) {
      next = insertColumnBefore(next, { ...degreeColumn }, "course");
      degreeIndex = next.findIndex((col) => col.id === "levelEducation");
    }
  }

  const updatedCourseIndex = next.findIndex((col) => col.id === "course");
  const updatedDegreeIndex = next.findIndex((col) => col.id === "levelEducation");
  if (updatedCourseIndex !== -1 && updatedDegreeIndex !== -1 && updatedDegreeIndex > updatedCourseIndex) {
    const [degreeColumn] = next.splice(updatedDegreeIndex, 1);
    const courseIdxAfterRemoval = next.findIndex((col) => col.id === "course");
    next.splice(courseIdxAfterRemoval, 0, degreeColumn);
  }

  // Forward migration: if Transfer Fee is visible, keep Certification / Preparatory after it.
  if (next.some((col) => col.id === "transferFee")) {
    if (!next.some((col) => col.id === "certificationEqualization")) {
      const certificationCol = tasksAllFieldsList.find((c) => c.id === "certificationEqualization");
      if (certificationCol) next = insertColumnAfter(next, { ...certificationCol }, "transferFee");
    }
    if (!next.some((col) => col.id === "preparatoryDepartment")) {
      const preparatoryCol = tasksAllFieldsList.find((c) => c.id === "preparatoryDepartment");
      const anchorId = next.some((c) => c.id === "certificationEqualization")
        ? "certificationEqualization"
        : "transferFee";
      if (preparatoryCol) next = insertColumnAfter(next, { ...preparatoryCol }, anchorId);
    }
  }

  // Only for old saves (snake_case / pre-taskId config): add missing default columns once.
  if (!legacyMigration) return next;

  if (!next.some((col) => col.id === "taskId")) {
    const taskIdCol = tasksAllFieldsList.find((c) => c.id === "taskId");
    if (taskIdCol) next = insertColumnAfter(next, { ...taskIdCol }, "date");
  }

  if (!next.some((col) => col.id === "studentId")) {
    const studentIdCol = tasksAllFieldsList.find((c) => c.id === "studentId");
    const anchorId = next.some((c) => c.id === "taskId") ? "taskId" : "date";
    if (studentIdCol) next = insertColumnAfter(next, { ...studentIdCol }, anchorId);
  }

  return next;
}
